import React, { useCallback, useEffect, useState } from 'react';
import { Heart, MessageCircle, Send } from 'lucide-react';
import { API, getAuthHeaders } from '../App';

/**
 * DashboardFeedPreview — shows the 3 most recent Club Feed posts on the
 * Dashboard homepage with real Like/Comment interactions. Reacts to
 * `bundle-feed-refresh` window events so the composer can trigger a refresh
 * after a new post is created.
 */
export default function DashboardFeedPreview({ user, onOpenPost, onSeeAll }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openComments, setOpenComments] = useState({}); // { post_id: [comments] | 'loading' }
  const [commentDraft, setCommentDraft] = useState({}); // { post_id: text }

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/social/feed?limit=3&skip=0`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (_err) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('bundle-feed-refresh', handler);
    // Poll every 15s so likes/comments from other users trickle in
    const timer = setInterval(load, 15000);
    return () => {
      window.removeEventListener('bundle-feed-refresh', handler);
      clearInterval(timer);
    };
  }, [load]);

  const toggleLike = async (postId) => {
    // Optimistic update
    setPosts((prev) => prev.map((p) => {
      if (p.post_id !== postId) return p;
      const likes = p.likes || [];
      const iLike = likes.includes(user?.user_id);
      return {
        ...p,
        likes: iLike ? likes.filter((u) => u !== user?.user_id) : [...likes, user?.user_id],
      };
    }));
    try {
      await fetch(`${API}/social/feed/${postId}/like`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
    } catch (_err) {
      load(); // revert on failure
    }
  };

  const toggleComments = async (postId) => {
    if (openComments[postId] && openComments[postId] !== 'loading') {
      setOpenComments((prev) => { const next = { ...prev }; delete next[postId]; return next; });
      return;
    }
    setOpenComments((prev) => ({ ...prev, [postId]: 'loading' }));
    try {
      const res = await fetch(`${API}/social/feed/${postId}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setOpenComments((prev) => ({ ...prev, [postId]: data.comments || [] }));
    } catch (_err) {
      setOpenComments((prev) => ({ ...prev, [postId]: [] }));
    }
  };

  const submitComment = async (postId) => {
    const content = (commentDraft[postId] || '').trim();
    if (!content) return;
    setCommentDraft((prev) => ({ ...prev, [postId]: '' }));
    try {
      await fetch(`${API}/social/feed/${postId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ content }),
      });
      // Optimistic add
      const newComment = {
        comment_id: `local_${Date.now()}`,
        post_id: postId,
        user_id: user?.user_id,
        user_name: user?.name,
        user_picture: user?.picture,
        content,
        created_at: new Date().toISOString(),
      };
      setOpenComments((prev) => ({
        ...prev,
        [postId]: [newComment, ...(Array.isArray(prev[postId]) ? prev[postId] : [])],
      }));
      setPosts((prev) => prev.map((p) => p.post_id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
    } catch (_err) {
      window.alert('Could not post comment');
    }
  };

  if (loading) return null;
  if (!posts.length) return null;

  return (
    <div className="mb-6" data-testid="dashboard-feed-preview">
      {posts.map((post) => (
        <DashboardPostCard
          key={post.post_id}
          post={post}
          user={user}
          isLiked={(post.likes || []).includes(user?.user_id)}
          onLike={() => toggleLike(post.post_id)}
          onToggleComments={() => toggleComments(post.post_id)}
          commentsOpen={openComments[post.post_id] !== undefined}
          comments={openComments[post.post_id]}
          commentDraft={commentDraft[post.post_id] || ''}
          setCommentDraft={(v) => setCommentDraft((prev) => ({ ...prev, [post.post_id]: v }))}
          onSubmitComment={() => submitComment(post.post_id)}
          onOpenPost={() => onOpenPost && onOpenPost(post.post_id)}
        />
      ))}
      <div className="text-center">
        <button
          type="button"
          onClick={onSeeAll}
          data-testid="dashboard-feed-see-all"
          className="text-xs font-bold text-emerald-700 hover:text-emerald-900 py-2 px-4"
        >
          See all in Bundle Feed →
        </button>
      </div>
    </div>
  );
}

function DashboardPostCard({
  post, user, isLiked, onLike, onToggleComments,
  commentsOpen, comments, commentDraft, setCommentDraft, onSubmitComment, onOpenPost,
}) {
  const likeCount = (post.likes || []).length;
  const commentCount = post.comment_count || 0;
  const media = post.media_url || post.photo_url || (Array.isArray(post.media_urls) ? post.media_urls[0] : null);
  const isVideo = media && (post.post_type === 'video' || (post.media_type || '').startsWith('video/') || (post.video_url && post.video_url === media));

  return (
    <div className="bg-white rounded-2xl border border-bundle-cream-dark shadow-sm mb-3 overflow-hidden" data-testid={`feed-preview-card-${post.post_id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        {post.is_ai_post ? (
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center text-white font-black text-[11px] flex-shrink-0">AI</div>
        ) : post.user_picture ? (
          <img src={post.user_picture} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-bundle-green/20 flex items-center justify-center text-sm font-bold text-bundle-green flex-shrink-0">
            {(post.user_name || 'M').charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm text-bundle-green truncate">{post.user_name || 'Member'}</p>
          <p className="text-[11px] text-gray-500">
            {post.post_type === 'round' && post.course_name
              ? `posted a round at ${post.course_name}`
              : post.post_type === 'photo' ? 'shared a photo'
              : post.post_type === 'video' ? 'shared a video'
              : formatWhen(post.created_at)}
          </p>
        </div>
      </div>

      {/* Body — caption */}
      {post.caption && (
        <button type="button" onClick={onOpenPost} className="block w-full text-left px-3 pb-2">
          <p className="text-sm text-gray-800 whitespace-pre-line line-clamp-4">{post.caption}</p>
        </button>
      )}

      {/* Round score card (round posts) */}
      {post.post_type === 'round' && post.score && (
        <button type="button" onClick={onOpenPost} className="block w-full text-left px-3 pb-2">
          <div className="bg-gradient-to-br from-bundle-green to-emerald-700 text-white rounded-xl p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">{post.course_name}</p>
            <p className="text-4xl font-black mt-1">{post.score}</p>
          </div>
        </button>
      )}

      {/* Media — gallery for multi-media posts (Dashboard composer with 2+ files) */}
      {(() => {
        const items = Array.isArray(post.media) && post.media.length > 0
          ? post.media
          : (Array.isArray(post.media_urls) && post.media_urls.length > 0
              ? post.media_urls.map((u) => ({ url: u, type: (post.video_url === u ? 'video' : 'photo') }))
              : (media ? [{ url: media, type: isVideo ? 'video' : 'photo' }] : []));
        if (items.length === 0) return null;
        if (items.length === 1) {
          const only = items[0];
          return only.type === 'video' ? (
            <video src={only.url} controls playsInline className="w-full max-h-96 object-contain bg-black" />
          ) : (
            <button type="button" onClick={onOpenPost} className="block w-full">
              <img src={only.url} alt="" className="w-full max-h-96 object-cover" loading="lazy" />
            </button>
          );
        }
        // 2+ items → cropped square gallery, cap at 4 shown with "+N more" overlay
        const shown = items.slice(0, 4);
        const extra = items.length - shown.length;
        const cols = shown.length === 2 ? 'grid-cols-2' : 'grid-cols-2';
        return (
          <button type="button" onClick={onOpenPost} className={`grid ${cols} gap-1 w-full`} data-testid={`feed-preview-gallery-${post.post_id}`}>
            {shown.map((m, i) => (
              <div key={m.url || i} className="relative aspect-square bg-black/5">
                {m.type === 'video' ? (
                  <>
                    <video src={m.url} muted playsInline preload="metadata" className="w-full h-full object-cover bg-black" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white text-sm">▶</div>
                    </div>
                  </>
                ) : (
                  <img src={m.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                )}
                {i === shown.length - 1 && extra > 0 && (
                  <div className="absolute inset-0 bg-black/50 text-white flex items-center justify-center text-xl font-black">
                    +{extra}
                  </div>
                )}
              </div>
            ))}
          </button>
        );
      })()}

      {/* Stats row */}
      {(likeCount > 0 || commentCount > 0) && (
        <div className="px-3 pt-2 flex items-center justify-between text-[11px] text-gray-500">
          <span>{likeCount > 0 ? `${likeCount} ${likeCount === 1 ? 'like' : 'likes'}` : ''}</span>
          <span>{commentCount > 0 ? `${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}` : ''}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-1 border-t border-bundle-cream-dark mt-2 p-1">
        <button
          type="button"
          onClick={onLike}
          data-testid={`feed-preview-like-${post.post_id}`}
          className={`flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm transition-colors ${isLiked ? 'text-rose-600 bg-rose-50' : 'text-gray-600 hover:bg-bundle-cream/60'}`}
        >
          <Heart className={`w-4 h-4 ${isLiked ? 'fill-rose-600' : ''}`} />
          Like
        </button>
        <button
          type="button"
          onClick={onToggleComments}
          data-testid={`feed-preview-comment-${post.post_id}`}
          className="flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm text-gray-600 hover:bg-bundle-cream/60 transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Comment
        </button>
      </div>

      {/* Comments panel */}
      {commentsOpen && (
        <div className="border-t border-bundle-cream-dark p-3 space-y-2 bg-bundle-cream/30">
          {comments === 'loading' ? (
            <p className="text-xs text-gray-500 italic">Loading comments…</p>
          ) : Array.isArray(comments) && comments.length > 0 ? (
            comments.slice(0, 5).map((c) => (
              <div key={c.comment_id} className="flex items-start gap-2" data-testid={`feed-preview-comment-row-${c.comment_id}`}>
                {c.user_picture ? (
                  <img src={c.user_picture} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-bundle-green/20 flex items-center justify-center text-[10px] font-bold text-bundle-green flex-shrink-0">
                    {(c.user_name || 'M').charAt(0)}
                  </div>
                )}
                <div className="flex-1 bg-white rounded-2xl px-3 py-2">
                  <p className="text-[11px] font-bold text-bundle-green">{c.user_name}</p>
                  <p className="text-sm text-gray-700 break-words">{c.content}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-xs text-gray-500 italic">Be the first to comment.</p>
          )}

          {/* Comment composer */}
          <div className="flex items-center gap-2 pt-1">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-bundle-green/20 flex items-center justify-center text-[10px] font-bold text-bundle-green flex-shrink-0">
                {(user?.name || 'M').charAt(0)}
              </div>
            )}
            <input
              type="text"
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmitComment(); } }}
              placeholder="Write a comment…"
              data-testid={`feed-preview-comment-input-${post.post_id}`}
              className="flex-1 rounded-full bg-white border border-bundle-cream-dark px-3 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={onSubmitComment}
              disabled={!commentDraft.trim()}
              data-testid={`feed-preview-comment-send-${post.post_id}`}
              className="p-1.5 rounded-full text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send comment"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return '';
  const now = new Date();
  const then = new Date(iso);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return then.toLocaleDateString();
}
